import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Bearer token for the affiliate session — kept in localStorage so the
// session survives page reload. Cookies are also set by the backend
// for browsers that allow third-party SameSite=Lax usage; the Bearer
// token is a belt-and-suspenders fallback that always works.
const TOKEN_KEY = "clipra_aff_token";

export function getAffToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setAffToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAffToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let pendingQueue = [];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes("/affiliate/login") &&
      !original.url?.includes("/affiliate/signup") &&
      !original.url?.includes("/affiliate/auth/refresh")
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject, original });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const { data } = await api.post("/affiliate/auth/refresh");
        if (data?.access_token) setAffToken(data.access_token);
        pendingQueue.forEach(({ resolve, original: o }) => resolve(api(o)));
        pendingQueue = [];
        return api(original);
      } catch (e) {
        pendingQueue.forEach(({ reject }) => reject(e));
        pendingQueue = [];
        setAffToken(null);
        if (
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/affiliate/login") &&
          !window.location.pathname.startsWith("/affiliate/signup") &&
          !window.location.pathname.startsWith("/affiliate/claim") &&
          !window.location.pathname.startsWith("/affiliate/forgot") &&
          !window.location.pathname.startsWith("/affiliate/reset")
        ) {
          window.location.href = "/affiliate/login";
        }
        throw e;
      } finally {
        isRefreshing = false;
      }
    }
    throw error;
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join("; ");
  return String(detail);
}

export default api;
